import { useState } from 'react';
import { Row, Col, Space, message, Dropdown, Upload, FormInstance } from 'antd';
import { useDispatch } from 'react-redux';
import { ExportOutlined } from "@ant-design/icons";
import { ReModal, ReButton } from "@/components/refineElement";
import { getOnOkFunction } from "@/lib/antdElementFunction";
import { getLangList } from '@/lib/i18n';
import { getDetailsArray } from "@/lib/user";
import { dateFormat, getMoment, getCurrentTime } from "@/lib/dateTime";
import { exportExcel } from "@/lib/exlsDownload";
import { excelUploadProps } from "@/lib/uploadFunction";
import { getFileTitleObject, exportUserOptions, excelTitleUser } from "@/lib/humanResourceManagement";
import { hasPermission } from "@/lib/permissionUtil";
import UserConfig from "@/config/user";
import { ModalProfile } from "@/components/modal";
import { getUserAccountById } from "@/stores/user/action";
import { StudentType } from './types';
import StudentTable from './StudentTable';

const { createUser, exportUserFile, importUserFile } = UserConfig;

type Props = {
  studentArray: StudentType[];
  classId: number;
  getAllStudentByClassFunction: Function;
  yearSemesterForm: FormInstance;
}

function Students(props: Props) {
  const { studentArray, classId, getAllStudentByClassFunction, yearSemesterForm } = props;
  const [studentInfo, setStudentInfo] = useState({} as StudentType);
  const [isModalProfileVisible, setIsModalProfileVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const dispatch = useDispatch();
  const { userAccountId } = studentInfo;
  const studentListT: any = getLangList("studentList");
  const profileInfoT: any = getLangList("profileInfo");
  const organizationT: any = getLangList("organization");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");
  const errorCodeT: any = getLangList("errorCode");
  const fileTitleObject = getFileTitleObject('STUDENT_WORK', 'STUDENT_RESIGN');
  const getUserAccountByIdFunction = (userAccountId: number) => () => {
    const params = { userAccountId };
    dispatch(getUserAccountById(params));
  }
  const getUserInfo = handleType === 'updateProfileInfo' ? getUserAccountByIdFunction(userAccountId) : () => { };
  const okUserFunction = {
    createUser: createUserFunction,
  };

  function toggleProfileModal(handleType: string = '') {
    setIsModalProfileVisible(!isModalProfileVisible);
    setHandleType(handleType);
  };

  function clickUserCreate() {
    setStudentInfo({} as StudentType);
    toggleProfileModal('createUser');
  };

  function clickUserExport(e: any) {
    const { key } = e;
    const { semesterId } = yearSemesterForm.getFieldsValue();
    const params = { personType: 1, isNeedLeave: key, semesterId };

    exportUserFile(params).then((res: any) => {
      const { success, value } = res;
      if (!success) {
        return message.error(errorT.failed);
      }
      const containRange = key === 'true' ? fileTitleObject.workAndResign : fileTitleObject.work;
      const fileTitle = `${organizationT.STUDENT}(${containRange})_${getCurrentTime().format(dateFormat)}`;
      const rowData = value.map((item: any) => [
        item.username,
        item.roleName,
        item.birthday ? getMoment(item.birthday).format(dateFormat) : '',
        item.nameCn,
        item.nameEn,
        item.gender,
        item.identityNumber,
        item.residenceAddress,
        item.mailingAddress,
        item.school,
        item.grade,
        item.classroom,
        item.seatNumber,
        item.allergy,
        item.cramClassName,
        item.cramClassSeatNum,
        item.isLunch === 1 ? 'TRUE' : 'FALSE',
        item.isSnack === 1 ? 'TRUE' : 'FALSE',
        item.extension,
        item.departmentName,
        item.dueDate ? getMoment(item.dueDate).format(dateFormat) : '',
        item.businessEmail,
        item.personalEmail,
        item.accessCardNumber,
        item.cellPhoneNumber,
        item.year,
        item.semesterName,
      ]);
      exportExcel({
        data: [excelTitleUser, ...rowData],
        workbookName: fileTitle,
      });
    });
  };

  function clickImportUserFile(e: any) {
    const { file, onSuccess, onError } = e;
    const params = { fileFormat: { file } };
    const isTooLarge = file.size / 1024 / 1024 > 50;

    if (isTooLarge) {
      message.error(errorT.IMAGE_TOO_LARGE);
      return onError();
    }

    importUserFile(params).then((res: any) => {
      if (!res.success) {
        message.error(errorCodeT[res.errorCode] ?? errorT.failed);
        return onError();
      }
      onSuccess();
      message.success(errorT.success);
      getAllStudentByClassFunction();
    });
  };

  function createUserFunction(values: any) {
    const {
      nameCn,
      nameEn,
      accessCardNumber,
      birthday,
      gender,
      residenceAddress,
      mailingAddress,
      identityNumber,
      school,
      grade,
      classroom,
      seatNumber,
      allergy,
      idCardImg,
      qrCode,
      isLunch,
      isSnack,
      cramClassSeatNum,
      username,
      password,
    } = values;
    const detailsArray = getDetailsArray(values);
    const userInfoData = {
      nameCn,
      nameEn,
      accessCardNumber,
      birthday: birthday?.format(dateFormat) ?? '',
      gender,
      residenceAddress,
      mailingAddress,
      identityNumber,
      school,
      grade,
      classroom,
      seatNumber,
      allergy,
      isLunch: isLunch ? 1 : 0,
      isSnack: isSnack ? 1 : 0,
      details: detailsArray,
      cramClassId: classId,
      cramClassSeatNum,
      username,
      password,
      roleId: '2',
      departmentId: '1',
    };
    const params = { stringFormat: { userInfo: userInfoData }, fileFormat: { idCardImg, qrCode } };

    createUser(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorCodeT[res.errorCode] ?? errorT.failed);
      }
      message.success(errorT.success);
      getAllStudentByClassFunction();
      toggleProfileModal();
    });
  };

  return (
    <>
      <Row gutter={[0, 12]} justify="end">
        <Col>
          <Space>
            {hasPermission('HR_MANAGEMENT_STUDENT_NEW') &&
              <ReButton iconName='plus' onClick={clickUserCreate}>
                {studentListT.REGISTER}
              </ReButton>
            }

            {hasPermission('HR_MANAGEMENT_STUDENT_NEW') &&
              <Upload {...excelUploadProps} customRequest={clickImportUserFile}>
                <ReButton iconName='import'>
                  {buttonT.IMPORT}
                </ReButton>
              </Upload>
            }

            <Dropdown.Button
              menu={{
                items: exportUserOptions(fileTitleObject),
                onClick: clickUserExport,
              }}
            >
              <ExportOutlined />
              {buttonT.EXPORT}
            </Dropdown.Button>
          </Space>
        </Col>

        <Col span={24}>
          <StudentTable
            studentArray={studentArray}
            fetchStudentFunction={getAllStudentByClassFunction}
          />
        </Col>
      </Row>

      {isModalProfileVisible &&
        <ReModal
          title={profileInfoT.PROFILE_INFO}
          onCancel={toggleProfileModal}
          visible={isModalProfileVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okUserFunction)}
          CollectedForm={ModalProfile}
          formProps={{
            getUserInfo,
            handleType,
          }}
          width={'700px'}
        />
      }
    </>
  )
}

export default Students;